import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Users,
  CheckCircle2,
  Tag,
  BookOpen,
} from 'lucide-react';

import PageLayout from '../components/layout/PageLayout';
import Button from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import RegistrationDialog from '../components/features/RegistrationDialog';
import { Skeleton } from '../components/ui/Skeleton';
import { api } from '../services/api';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/contato'],
      ['Área Administrativa', '/admin'],
    ],
  },
];

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    api.getEvents().then((events) => {
      const found = events.find((e) => e.id === id);
      setEvent(found || null);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
        <div className="app-container py-6">
          <Skeleton className="h-64 w-full rounded-2xl" />
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <Skeleton className="h-64 w-full rounded-2xl lg:col-span-2" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!event) {
    return (
      <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
        <div className="app-container py-16 text-center">
          <p className="font-heading text-xl font-extrabold text-ink">Evento não encontrado</p>
          <p className="mt-1 text-sm text-ink-soft">O evento que você procura não existe ou foi removido.</p>
          <Link
            to="/evento"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-primary-dark"
          >
            <ArrowLeft size={16} /> Voltar para Eventos
          </Link>
        </div>
      </PageLayout>
    );
  }

  const date = parseISO(event.date);
  const remaining = event.totalSlots - event.filledSlots;
  const soldOut = remaining <= 0;
  const pct = Math.min(100, Math.round((event.filledSlots / event.totalSlots) * 100));

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <div className="app-container py-6">
        <Link
          to="/evento"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary-dark"
        >
          <ArrowLeft size={16} /> Voltar para Eventos
        </Link>

        {/* Hero */}
        <div className="relative mt-4 overflow-hidden rounded-2xl shadow-lift">
          <img src={event.cover} alt={event.title} className="h-56 w-full object-cover md:h-80" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-2">
              {soldOut ? (
                <Badge tone="danger">Esgotado</Badge>
              ) : (
                <Badge tone="success">Inscrições abertas</Badge>
              )}
              <Badge tone="accent">
                <CalendarDays size={12} /> {event.days}
              </Badge>
            </div>
            <h1 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold leading-tight text-white md:text-4xl">
              {event.title}
            </h1>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main content */}
          <div className="space-y-6 lg:col-span-2">
            {/* Sobre */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft md:p-6">
              <h2 className="font-heading text-lg font-extrabold text-ink">Sobre o evento</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-soft">{event.description}</p>
            </section>

            {/* Assuntos */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-extrabold text-ink">
                <BookOpen size={18} className="text-primary" /> Assuntos abordados
              </h2>
              <ul className="mt-4 space-y-2.5">
                {event.subjects.map((subject, i) => (
                  <li key={subject} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-light font-heading text-xs font-bold text-primary">
                      {i + 1}
                    </span>
                    <span className="text-sm font-medium text-ink">{subject}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
              <h3 className="font-heading text-base font-extrabold text-ink">Informações</h3>
              <dl className="mt-4 space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary">
                    <CalendarDays size={17} />
                  </span>
                  <div>
                    <dt className="font-semibold text-ink">Datas</dt>
                    <dd className="text-ink-soft">{event.days}</dd>
                    <dd className="text-xs text-ink-faint">
                      {format(date, "EEEE, dd 'de' MMMM · HH:mm", { locale: ptBR })}
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-light text-accent-dark">
                    <MapPin size={17} />
                  </span>
                  <div>
                    <dt className="font-semibold text-ink">Local</dt>
                    <dd className="text-ink-soft">{event.location}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                    <Tag size={17} />
                  </span>
                  <div>
                    <dt className="font-semibold text-ink">Taxa de inscrição</dt>
                    <dd className="font-heading font-bold text-primary">{event.fee}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
                    <Users size={17} />
                  </span>
                  <div className="w-full">
                    <dt className="font-semibold text-ink">Vagas</dt>
                    <dd className="text-ink-soft">
                      {event.filledSlots}/{event.totalSlots} preenchidas
                    </dd>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${soldOut ? 'bg-rose-400' : 'bg-primary'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </dl>

              <div className="mt-5 border-t border-slate-100 pt-4">
                {registered ? (
                  <Button variant="outline" className="w-full" disabled>
                    <CheckCircle2 size={17} className="text-emerald-500" /> Inscrição confirmada
                  </Button>
                ) : (
                  <Button
                    variant={soldOut ? 'outline' : 'accent'}
                    className="w-full"
                    disabled={soldOut}
                    onClick={() => setActive(true)}
                  >
                    {soldOut ? 'Vagas esgotadas' : 'Fazer inscrição'}
                  </Button>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <RegistrationDialog
        event={active ? event : null}
        onClose={() => setActive(false)}
        onSuccess={() => setRegistered(true)}
      />
    </PageLayout>
  );
}
